import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { SearchableSelect, useToast } from '@kss/ui';
import { COUNTRIES, INDIAN_CITIES, INDIAN_STATES } from '../utils/indiaLocations';
import { lookupPincode } from '../utils/pincodeLookup';

/**
 * Reusable address picker shared by Store / Warehouse / Company /
 * Employee forms.
 *
 * Behaviour:
 *   - PIN code field auto-completes city + state + country when the
 *     user types/blurs a 6-digit pincode (api.postalpincode.in).
 *   - City and State are SearchableSelects with a static fallback list
 *     (top metros / 28 states + 8 UTs) so users can pick offline; both
 *     accept custom values via `allowCustom` so a typed-in entry is
 *     still respected.
 *   - Country is a SearchableSelect over the top 20 countries with
 *     India pinned at the top.
 *
 * The component is fully controlled — the parent owns each piece of
 * state and provides setters. This keeps it trivial to plug into the
 * existing flat form-state model used by NewStore / NewCompany.
 */

const inputCls =
  'h-10 w-full rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';

/**
 * Map common country names returned by the postalpincode.in API to
 * their ITU-T E.164 international calling codes (ISD codes). The live
 * backend stores `country` in a `varchar(5)` column; sending the
 * full name like "India" or "United States" → 500 SQL truncation,
 * sending "91"/"1" round-trips cleanly AND pairs naturally with phone
 * numbers (the rest of the row is "+91 9876…").
 *
 * Companion table `STATE_NAME_TO_GST_CODE` does the same for state
 * — postalpincode.in returns "Maharashtra" but the form value is
 * the 2-digit GST state code "27" (matches the leading digits of
 * any GSTIN issued in that state).
 *
 * Anything we don't recognise gets truncated to its first 5 chars
 * so an exotic upstream response can't crash the save.
 */
const COUNTRY_NAME_TO_ISD: Record<string, string> = {
  india: '91',
  pakistan: '92',
  bangladesh: '880',
  bhutan: '975',
  nepal: '977',
  'sri lanka': '94',
  maldives: '960',
  'united states': '1',
  'united states of america': '1',
  'united kingdom': '44',
  'united arab emirates': '971',
  australia: '61',
  canada: '1',
  china: '86',
  germany: '49',
  indonesia: '62',
  japan: '81',
  malaysia: '60',
  'saudi arabia': '966',
  singapore: '65',
  thailand: '66',
};

function toIsoCountry(name: string): string {
  const key = name.trim().toLowerCase();
  return COUNTRY_NAME_TO_ISD[key] ?? name.slice(0, 5);
}

const STATE_NAME_TO_GST_CODE: Record<string, string> = {
  'jammu and kashmir': '01',
  'himachal pradesh': '02',
  punjab: '03',
  chandigarh: '04',
  uttarakhand: '05',
  haryana: '06',
  delhi: '07',
  rajasthan: '08',
  'uttar pradesh': '09',
  bihar: '10',
  sikkim: '11',
  'arunachal pradesh': '12',
  nagaland: '13',
  manipur: '14',
  mizoram: '15',
  tripura: '16',
  meghalaya: '17',
  assam: '18',
  'west bengal': '19',
  jharkhand: '20',
  odisha: '21',
  chhattisgarh: '22',
  'madhya pradesh': '23',
  gujarat: '24',
  'dadra and nagar haveli and daman and diu': '26',
  maharashtra: '27',
  karnataka: '29',
  goa: '30',
  lakshadweep: '31',
  kerala: '32',
  'tamil nadu': '33',
  puducherry: '34',
  'andaman and nicobar islands': '35',
  telangana: '36',
  'andhra pradesh': '37',
  ladakh: '38',
};

function toGstStateCode(name: string): string {
  const key = name.trim().toLowerCase();
  return STATE_NAME_TO_GST_CODE[key] ?? name.slice(0, 5);
}

export interface AddressFieldsProps {
  addressLine1: string;
  setAddressLine1: (v: string) => void;
  addressLine2?: string;
  setAddressLine2?: (v: string) => void;
  /** When false, the Address Line 2 input is hidden. Default true. */
  showLine2?: boolean;

  pincode: string;
  setPincode: (v: string) => void;

  city: string;
  setCity: (v: string) => void;

  state: string;
  setState: (v: string) => void;

  /**
   * Country and its setter are optional — Warehouse rows on the live
   * API don't include a country column, so the field can be hidden by
   * omitting `setCountry`.
   */
  country?: string;
  setCountry?: (v: string) => void;
}

export default function AddressFields({
  addressLine1,
  setAddressLine1,
  addressLine2,
  setAddressLine2,
  showLine2 = false,
  pincode,
  setPincode,
  city,
  setCity,
  state,
  setState,
  country,
  setCountry,
}: AddressFieldsProps) {
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const [resolving, setResolving] = useState(false);
  const lastResolvedPin = useRef<string>('');

  /**
   * Trigger the pincode lookup whenever the user has typed exactly 6
   * digits AND we haven't already resolved that pin during this mount.
   * Debounced ~500ms so the lookup doesn't fire on every keystroke.
   */
  useEffect(() => {
    const pin = (pincode ?? '').trim();
    if (!/^\d{6}$/.test(pin)) return;
    if (lastResolvedPin.current === pin) return;
    const timer = setTimeout(async () => {
      setResolving(true);
      try {
        const res = await lookupPincode(pin);
        if (!res) {
          toastRef.current.info(
            'PIN not found',
            'Could not auto-fill city/state for this pincode — please pick manually.',
          );
          return;
        }
        lastResolvedPin.current = pin;
        if (res.city) setCity(res.city);
        // postalpincode.in returns full state names ("Maharashtra").
        // The form value is the GST 2-digit state code ("27") so the
        // SearchableSelect highlights the matching option.
        if (res.state) setState(toGstStateCode(res.state));
        // postalpincode.in returns full names ("India"). The live API
        // stores country as a 2-letter ISO code (varchar(5)) so map
        // here before setting state. Anything we don't recognise gets
        // truncated to its first 5 chars to avoid the SQL truncation
        // 500 the backend currently throws.
        if (res.country && setCountry) {
          setCountry(toIsoCountry(res.country));
        }
        toastRef.current.success(
          'Address auto-filled',
          `${res.city}, ${res.state}`,
        );
      } finally {
        setResolving(false);
      }
    }, 500);
    return () => clearTimeout(timer);
    // We intentionally run only on `pincode` changes — the setters are
    // stable callbacks from the parent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pincode]);

  return (
    <>
      <label className="flex flex-col gap-1 md:col-span-2">
        <span className="text-xs font-medium text-content-secondary">Address line 1</span>
        <input
          value={addressLine1}
          onChange={(e) => setAddressLine1(e.target.value)}
          placeholder="Street, area, landmark"
          maxLength={200}
          className={inputCls}
        />
      </label>

      {showLine2 && setAddressLine2 && (
        <label className="flex flex-col gap-1 md:col-span-2">
          <span className="text-xs font-medium text-content-secondary">Address line 2</span>
          <input
            value={addressLine2 ?? ''}
            onChange={(e) => setAddressLine2(e.target.value)}
            placeholder="Building, floor (optional)"
            maxLength={200}
            className={inputCls}
          />
        </label>
      )}

      <label className="flex flex-col gap-1">
        <span className="flex items-center gap-1 text-xs font-medium text-content-secondary">
          PIN code
          {resolving && <Loader2 className="h-3 w-3 animate-spin text-content-muted" />}
        </span>
        <input
          inputMode="numeric"
          pattern="\d*"
          value={pincode}
          // Strip non-digits as the user types so the regex check above
          // can fire reliably without surprises (copy-paste from a Word
          // doc often introduces non-breaking spaces).
          onChange={(e) => setPincode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="400050"
          maxLength={6}
          className={`${inputCls} font-mono`}
        />
      </label>

      <SearchableSelect
        label="City"
        value={city}
        onChange={setCity}
        options={INDIAN_CITIES}
        placeholder="Pick or type a city"
        searchPlaceholder="Search city…"
        allowCustom
      />

      <SearchableSelect
        label="State"
        value={state}
        onChange={setState}
        options={INDIAN_STATES}
        placeholder="Pick a state"
        searchPlaceholder="Search state…"
        allowCustom
      />

      {setCountry && (
        <SearchableSelect
          label="Country"
          value={country ?? ''}
          onChange={setCountry}
          options={COUNTRIES}
          placeholder="Pick a country"
          searchPlaceholder="Search country…"
          allowCustom
        />
      )}
    </>
  );
}
